import { PosState } from "@unleashed-admin/stores/PosSlice";
import { isMembership, unassignAllParticipantsFromMembership } from "./MembershipAllocationUtilsHelper";
import { CartItem, PrePurchasedItem, PurchaseItemViewModel } from "../models";
import { PointOfSaleCategory } from "../enums/PointOfSaleCategory";
import { findSimilarProduct, removeItemFromCart } from "./Product";
import { ProductFee } from "../models/ProductFee";

export const getProductIteemFeeIdOrProductFeeId = (
  product:
    | CartItem
    | PrePurchasedItem
    | PurchaseItemViewModel
    | undefined
    | null
): number => {
  if (product === undefined || product === null) return 0;
  if ("itemFeeId" in product && product.itemFeeId) return product.itemFeeId;
  if ("productFeeId" in product && product.productFeeId) return product.productFeeId;

  return 0;
};

export const isProductFee = (
  item: CartItem | PrePurchasedItem | PurchaseItemViewModel
) => item.type === PointOfSaleCategory.Fee && item.productFeeId != null;

export const isItemFee = (
  item: CartItem | PrePurchasedItem | PurchaseItemViewModel
) => item.type === PointOfSaleCategory.Fee && item.itemFeeId != null;

export const findFeeById = (itemsInCart:CartItem[], itemToCheck:CartItem, isProductFeeId:boolean ) => {
  return itemsInCart.find((currentItem:CartItem) => {
    if (isProductFeeId && "productFee" in itemToCheck) {
      return currentItem.isProductFee && currentItem?.productFeeId == itemToCheck?.productFee?.id;
    } else if ("itemFee" in itemToCheck) {
      return currentItem.isItemFee && currentItem?.itemFeeId == itemToCheck?.itemFee?.id;
    }
    return null;
  });
};

export const updateFeeQuantity = (state: PosState, removedItem:CartItem) => {
  if (!isMembership(removedItem)) {
    return;
  }

  if (removedItem.itemFee) {
    const feeItemInCart = findFeeById(state.itemsInCart ?? [], removedItem, false) as CartItem;
    if (feeItemInCart) {
      feeItemInCart.quantity = feeItemInCart.quantity > removedItem.quantity ? removedItem.quantity : feeItemInCart.quantity;
    }
  }
  
  if (removedItem.productFee) {
    const productfeeItemInCart = findFeeById(state.itemsInCart ?? [], removedItem, true) as CartItem;
    if (productfeeItemInCart) {
      productfeeItemInCart.quantity = productfeeItemInCart.quantity > removedItem.quantity ? removedItem.quantity : productfeeItemInCart.quantity;
    }
  }
};

export const handleFeeRemoval = (state:PosState, removedItem: CartItem) => {

  const existingProduct = findSimilarProduct(
    state.itemsInCart ?? [],
    removedItem
  ) as CartItem;

  if (removedItem.type == PointOfSaleCategory.Fee) {
    if (existingProduct) {
      removeItemFromCart(state, existingProduct);
    }
    return;
  }

  if (isMembership(removedItem)) {

    const feeItemInCart = findFeeById(state.itemsInCart ?? [], removedItem, false) as CartItem;
    const productfeeItemInCart = findFeeById(state.itemsInCart ?? [], removedItem, true) as CartItem;

    unassignAllParticipantsFromMembership(state.selectedAccount, existingProduct);
    
    removeItemFromCart(state, existingProduct);

    if (feeItemInCart) {
      const allItemsWithFee = state.itemsInCart?.filter(r=>r?.itemFee?.id == feeItemInCart.itemFeeId) ?? [];
      // if there are multiple different memberships with same item fee and we are deleting only one ... subtract the quantity of Item to remove from item fee quantity 
      if (allItemsWithFee.length > 0) {
        const finalQty = feeItemInCart.quantity - existingProduct.quantity;
        feeItemInCart.quantity = finalQty >= 0 ? finalQty : 0;
      } else {
        removeItemFromCart(state, feeItemInCart);
      }
    }

    if (productfeeItemInCart) {
      // we are removing product fee item from cart 
      //check if any other item in cart has a product fee and add the max fee to cart
      removeItemFromCart(state, productfeeItemInCart);
      const newMaxProductFeeInCart = findMaxProductFeeInCart(state);

      if (newMaxProductFeeInCart) {
        state.itemsInCart?.push(newMaxProductFeeInCart);
      }
    }
  }
};

export const isNewProductFeeGreaterThanExisting = (state:PosState, potentialFeeItem:ProductFee) => {

  const existingProductFeeItem = state?.itemsInCart?.find(r => r.isProductFee === true);

  if (existingProductFeeItem) {
    return (potentialFeeItem?.amount ?? 0) > (existingProductFeeItem?.price ?? 0);
  }

  return false;
};

export const findMaxProductFeeInCart = (state:PosState,) => {
  const newMaxProductFee = state?.itemsInCart?.reduce((max?:ProductFee, r?:CartItem) => {
    // if no productFee or amount, skip
    if (!r?.productFee?.amount) return max;

    if ((r?.productFee?.amount ?? 0) > (max?.amount ?? 0)) {
      return r.productFee;
    }
    return max;
  }, undefined);

  if (newMaxProductFee) {
    return { name: newMaxProductFee.description, price: newMaxProductFee.amount, productFeeId: newMaxProductFee.id, color:"", type: PointOfSaleCategory.Fee, isForKds:false, quantity:1, isProductFee:true,  isFromCheckInTab:true } as CartItem;
  } 
  return null;
};

export const handleAddingProductFeeToCart = (state:PosState, productFee:ProductFee) => {

  const newProductFeeItem:CartItem = { name: productFee.description, price: productFee.amount, productFeeId: productFee.id, color:"", type: PointOfSaleCategory.Fee, isForKds:false, quantity:1, isProductFee:true,  isFromCheckInTab:true } as CartItem;

  // if no product fee in cart, add current fee to cart
  if (!state?.itemsInCart?.some(r => r.isProductFee === true)) {
    state.itemsInCart?.push(newProductFeeItem);
    return;
  }

  const sameFeeItem = findSimilarProduct(state?.itemsInCart ?? [], newProductFeeItem) as CartItem;

  if (sameFeeItem) {
    return; // only 1 (qty) product fee per cart
  } else {
    if (isNewProductFeeGreaterThanExisting(state, productFee)) {
      const existingProductFeeItem = state?.itemsInCart?.find(r => r.isProductFee === true);

      if (existingProductFeeItem && ((productFee?.amount ?? 0) > (existingProductFeeItem?.price ?? 0))) {
        removeItemFromCart(state, existingProductFeeItem);
        state.itemsInCart?.push(newProductFeeItem);
      }

    }
  }
};